import httpClient from '~/lib/http-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toQueryString } from '~/utils/queryString';
import endpoints from './endpoints';
import { queryKeys } from './queryKeys';

export const getListAnnouncement = (params): Promise<any> =>
  httpClient.json.get(
    `${endpoints.announcement.getAnnouncements}?${toQueryString(params, {
      arrayFormat: 'none',
    })}`,
  );

export const getAnnouncement = (id: string): Promise<any> =>
  httpClient.json.get(endpoints.announcement.getAnnouncementDetail(id));

export const createAnnouncement = (params) =>
  httpClient.formData.post(endpoints.announcement.create, params);

export const updateAnnouncement = (params: any) => {
  // Extract id from FormData if it exists, otherwise use params.id
  const id = params instanceof FormData ? params.get('id') : params.id;
  return httpClient.formData.put(endpoints.announcement.update(id), params);
};

export const deleteAnnouncement = (id: string): Promise<any> =>
  httpClient.json.delete(endpoints.announcement.delete(id));

export const endPostAnnouncement = (id: string): Promise<any> =>
  httpClient.json.post(endpoints.announcement.endPost(id), {});

export const getAnnouncementLog = (id: string): Promise<any> =>
  httpClient.json.get(endpoints.announcement.getLog(id));

export const useGetListAnnouncement = (params) =>
  useQuery({
    queryKey: [queryKeys.announcement.list, params],
    queryFn: () => getListAnnouncement(params),
    select: (res) => res?.data,
  });

export const useGetAnnouncementDetail = ({
  enabled = true,
  onSuccess = () => {},
  onError = () => {},
  id,
}) =>
  useQuery({
    queryKey: [queryKeys.announcement.detail, id],
    queryFn: () => getAnnouncement(id),
    onSuccess,
    onError,
    enabled,
    retry: false,
    select: (res) => res?.data,
  });

export const useCreateAnnouncementMutation = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (resp: any) => void;
    onError?: (err: any) => void;
  } = {
    onSuccess: () => {},
    onError: () => {},
  },
) =>
  useMutation({
    mutationFn: createAnnouncement,
    onSuccess,
    onError,
  });

export const useUpdateAnnouncementMutation = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (resp: any) => void;
    onError?: (err: any) => void;
  } = {
    onSuccess: () => {},
    onError: () => {},
  },
) =>
  useMutation({
    mutationFn: updateAnnouncement,
    onSuccess,
    onError,
  });

export const useDeleteAnnouncementMutation = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (resp: any) => void;
    onError?: (err: any) => void;
  } = {
    onSuccess: () => {},
    onError: () => {},
  },
) =>
  useMutation({
    mutationFn: deleteAnnouncement,
    onSuccess,
    onError,
  });

export const useEndPostAnnouncementMutation = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (resp: any) => void;
    onError?: (err: any) => void;
  } = {
    onSuccess: () => {},
    onError: () => {},
  },
) =>
  useMutation({
    mutationFn: endPostAnnouncement,
    onSuccess,
    onError,
  });

export const useGetAnnouncementLog = ({
  enabled = true,
  id,
}: {
  enabled?: boolean;
  id?: string;
}) =>
  useQuery({
    queryKey: [queryKeys.announcement.detail, id, 'log'],
    queryFn: () => getAnnouncementLog(id!),
    enabled: enabled && !!id,
    retry: false,
    select: (res) => res?.data,
  });
